import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import MagnifierCarousel from "@/components/MagnifierCarousel";
import archive1 from "@/assets/archive-1.jpg";
import archive2 from "@/assets/archive-2.jpg";
import shoreAsset from "@/assets/story-shore.jpg.asset.json";
import planeRunwayAsset from "@/assets/story-plane-runway.jpg.asset.json";
import planeBeachAsset from "@/assets/story-plane-beach.jpg.asset.json";
import islandCliffAsset from "@/assets/story-island-cliff.jpg.asset.json";
import cargoShipAsset from "@/assets/story-cargo-ship.jpg.asset.json";
import { cmsQueryOptions, field, richText } from "@/lib/cms";

export const Route = createFileRoute("/story")({
  head: () => ({
    meta: [
      { title: "Backstory — Double Lion" },
      { name: "description", content: "The story behind Double Lion: quiet craftsmanship, materials that age gracefully, and the two lions stamped into every case back." },
      { property: "og:title", content: "Backstory — Double Lion" },
      { property: "og:description", content: "The story behind Double Lion: quiet craftsmanship, materials that age gracefully, and the two lions stamped into every case back." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://double-lion-muse.lovable.app/story" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://double-lion-muse.lovable.app/story" },
    ],
  }),
  component: StoryPage,
});

function StoryPage() {
  const { data: cms } = useQuery(cmsQueryOptions);
  const story = cms?.story;
  const body = richText(
    story?.body,
    [
      "Double Lion was founded on a simple idea: the best objects do one thing exceptionally well. No excess. No fragility. Just clarity. Every Double Lion binocular is built as a tool first — rugged, considered, and made to be used.",
      "The company was started by two brothers, Ethan and Ari, who grew up on the water. Between boat trips, fishing, and chasing light with a camera, a good pair of binoculars was never far from reach. They wanted something small enough to live in a jacket pocket, light enough to forget you were carrying, and sharp enough to trust when it mattered — whether that meant reading a channel marker at dusk or spotting a bird working bait a mile offshore.",
      "Every detail follows that intent. The 6x16 a roof-prism binocular with 16 mm objective lenses,6X zoom, fully multi-coated glass, and a 145 m field of view at 1,000 meters. It weighs 145 grams and measures 77 × 61 × 37 mm, so it travels as easily as a phone. The housing is machined aluminum with a hard anodized finish, splash-proof for life on deck, and shaped to focus quickly with one hand.",
      "Ethan and Ari believe in traveling light and seeing more. Double Lion is built for the same — for the people who keep a pair within reach on a boat, on a trail, on a plane to somewhere new, and use them every chance they get.",
    ].join("\n\n"),
  );
  const paragraphs = body.split(/\n\n+/).filter((p) => p.trim().length > 0);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-24 pb-32">
        {/* Hero image */}
        <section className="px-6 pt-8">
          <div className="mx-auto max-w-6xl">
            <div className="relative w-full aspect-video overflow-hidden bg-black">
              <img
                src={shoreAsset.url}
                alt="Shoreline at dusk where Double Lion was founded"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/40" />
              <div className="absolute inset-0 flex items-center justify-center">
                <h1 className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-background bg-black/30 px-4 py-2 backdrop-blur-sm">
                  The Double Lion Story
                </h1>
              </div>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center">
              Shoreline at dusk — where the idea started.
            </p>
          </div>
        </section>


        {/* Image band */}
        <section className="px-6 mt-20 md:mt-28">
          <div className="mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
            <div className="w-full">
              <MagnifierCarousel
                images={[{ display: planeRunwayAsset.url, hires: planeRunwayAsset.url }]}
                zoom={3}
              />
            </div>
            <div className="w-full">
              <MagnifierCarousel
                images={[{ display: cargoShipAsset.url, hires: cargoShipAsset.url }]}
                zoom={3}
              />
            </div>
          </div>
        </section>

        {/* Editorial body */}
        <section className="px-6 mt-20 md:mt-32">
          <div className="mx-auto max-w-3xl space-y-8 font-sans text-base md:text-lg text-foreground/80 leading-relaxed">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>


        {/* CTA */}
        <section className="px-6 mt-20 md:mt-28">
          <div className="mx-auto max-w-3xl">
            <Link
              to="/"
              className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b border-foreground pb-1 hover:opacity-70 transition-opacity"
            >
              Explore the product →
            </Link>
          </div>
        </section>
      </main>

    </div>
  );
}
